import React, { useEffect, useState, useRef } from "react";
import moment from "moment";
import _ from "lodash";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { CSVLink } from "react-csv";
import Layout from "../../components/Layout";
import Pagination from "../../components/Pagination";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Users, Calendar, Search, Download, Filter, Activity, ChevronDown } from "lucide-react";
import MemberSelect from "../../components/MemberSelect";
import DateBox from "../../components/DateBox";
import TimeList from "./TimeList";
import { getTimesheet } from "../../services";

export default function Timesheet() {
  const [startDate, setStartDate] = useState(moment().startOf("month").toDate());
  const [endDate, setEndDate] = useState(moment().toDate());
  const [selectedMember, setSelectedMember] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [timesheetData, setTimesheetData] = useState([]);
  const [errMsg, setErrMsg] = useState({ err: false, msg: "", color: "warning" });
  const [offset, setOffset] = useState(0);
  const [isLoadingCSV, setIsLoadingCSV] = useState(false);
  const itemsPerPage = 10;
  const [totalCount, setTotalCount] = useState(0);
  const [cSVData, setcSVData] = useState([]);
  const [showDatePicker, setShowDatePicker] = useState(false);

  const members = useSelector((state) => state.members.members);
  const userProfile = useSelector((state) => state.user.userinfo);
  const csvLinkRef = useRef();

  const notify = (message) => {
    toast.error(message, {
      position: "top-right",
      className: "dark:bg-gray-900",
    });
  };

  const setDateRange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const fetchTime = async (offset) => {
    setErrMsg({ err: false, msg: "", color: "warning" });
    if (_.isNil(startDate) || _.isNil(endDate)) {
      setErrMsg({ err: true, msg: "Please select valid dates", color: "red" });
      notify("Please select valid dates");
      return;
    }

    setIsLoading(true);
    try {
      const formattedStartDate = moment(startDate).format("YYYY-MM-DD");
      const formattedEndDate = moment(endDate).format("YYYY-MM-DD");

      const response = await getTimesheet(
        userProfile.MemberRole == 2 ? selectedMember : userProfile.Id,
        formattedStartDate,
        formattedEndDate,
        offset,
        itemsPerPage
      );
      setTimesheetData(response?.data?.Result?.response);
      setTotalCount(response?.data?.Result?.TotalCount);
    } catch (err) {
      console.error("Error fetching time:", err);
      notify("Error fetching timesheet data!");
    } finally {
      setIsLoading(false);
    }
  };
  const FetchSCVData = async () => {
    setErrMsg({ err: false, msg: "", color: "warning" });

    if (_.isNil(startDate) || _.isNil(endDate)) {
      setErrMsg({ err: true, msg: "Please select valid dates", color: "red" });
      notify("Please select valid dates");
      return;
    }

    setIsLoadingCSV(true);
    try {
      const formattedStartDate = moment(startDate).format("YYYY-MM-DD");
      const formattedEndDate = moment(endDate).format("YYYY-MM-DD");

      const response = await getTimesheet(
        selectedMember,
        formattedStartDate,
        formattedEndDate,
        0,
        totalCount
      );

      if (response?.data?.Result?.response) {
        if (response.data.Result.response.length === 0) {
          // Agar data nahi hai to button bhi hide karna hai
          setcSVData([]);
        } else {
          setcSVData(response.data.Result.response);
        }
      } else {
        setcSVData([]); // Data empty set karna
      }

    } catch (err) {
      console.error("Error fetching time:", err);
    } finally {
      setIsLoadingCSV(false);
    }
  };


  const prepareCSVData = () => {
    return cSVData.map((entry) => ({
      MemberName: entry.MemberName || "N/A",
      Date: moment(entry.CheckInTime).format("DD/MM/YYYY"),
      CheckInTime: moment(entry.CheckInTime).format("h:mm A"),
      CheckOutTime: moment(entry.CheckOutTime).format("h:mm A"),
      TotalHours: `${entry.TotalHours} hrs ${entry.TotalMins} m`,
      workHours: entry.workHours,
    }));
  };
  const csvHeaders = [
    { label: "Member Name", key: "MemberName" },
    { label: "Date", key: "Date" },
    { label: "CheckIn Time", key: "CheckInTime" },
    { label: "CheckOut Time", key: "CheckOutTime" },
    { label: "TotalHours", key: "TotalHours" },
  ];
  const handleCSVDownload = async () => {
    try {
      setIsLoadingCSV(true);
      await FetchSCVData();

      // Check if CSV data is empty
      if (cSVData.length === 0) {
        setIsLoadingCSV(false);
        return;
      }

      // Ensure we have data before triggering download
      if (csvLinkRef.current) {
        csvLinkRef.current.link.click();
      }
    } catch (error) {
      console.error("Error downloading CSV:", error);
    } finally {
      setIsLoadingCSV(false);
    }
  };
  useEffect(() => {
    fetchTime(offset);
  }, [offset]);

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="px-4 sm:px-6 mb-8"
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-500/10 rounded-xl">
              <Clock className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Timesheet</h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm">Track and manage member attendance and working hours</p>
            </div>
          </div>

          <div className="flex items-center gap-3 self-end sm:self-auto">
            {timesheetData.length > 0 && (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="button"
                className={`flex items-center gap-2 px-5 py-2.5 bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 font-bold rounded-xl border border-blue-100 dark:border-blue-500/20 hover:bg-blue-100 dark:hover:bg-blue-500/20 transition-all active:scale-95 shadow-sm hover:shadow-blue-500/10 ${isLoadingCSV ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                onClick={handleCSVDownload}
                disabled={isLoadingCSV}
              >
                {isLoadingCSV ? (
                  <svg className="w-4 h-4 animate-spin text-blue-600" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <Download className="w-5 h-5" />
                )}
                Download CSV
              </motion.button>
            )}
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mx-4 sm:mx-6 mb-6 p-6 bg-white dark:bg-gray-900/50 backdrop-blur-xl border border-gray-100 dark:border-blue-gray-900 rounded-2xl shadow-xl shadow-gray-200/50 dark:shadow-none relative z-[30]"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[0.8fr_0.8fr_0.6fr] gap-5 items-end">
          {userProfile.MemberRole == 2 && (
            <div className="space-y-2">
              <label className="block text-sm text-gray-700 dark:text-gray-300 ms-1 flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-400" />
                Member
              </label>
              <MemberSelect
                members={members}
                selectedMember={selectedMember}
                onSelect={setSelectedMember}
                placeholder="All Members"
              />
            </div>
          )}

          <div className="space-y-2">
            <label className="block text-sm text-gray-700 dark:text-gray-300 ms-1 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              Date Range
              
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowDatePicker(!showDatePicker)}
                className="w-full flex items-center justify-between p-1.5 bg-gray-50 dark:bg-black/50 border border-gray-100 dark:border-blue-gray-900 rounded-xl text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500/20 outline-none transition-all group"
              >
                <div className="flex items-center gap-2 flex-1">
                  <div className="bg-white dark:bg-white/10 px-3 py-2 rounded-lg font-medium tracking-wide shadow-sm dark:shadow-none border border-gray-100 dark:border-transparent text-[13px] truncate">
                    {startDate ? moment(startDate).format("DD MMM YYYY") : "Start Date"}
                  </div>
                  <span className="text-gray-400 dark:text-gray-500 font-medium text-xs">To</span>
                  <div className="bg-white dark:bg-white/10 px-3 py-2 rounded-lg font-medium tracking-wide shadow-sm dark:shadow-none border border-gray-100 dark:border-transparent text-[13px] truncate">
                    {endDate ? moment(endDate).format("DD MMM YYYY") : "End Date"}
                  </div>
                </div>
                <div className="px-3 border-l border-gray-100 dark:border-white/10 ml-2">
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${showDatePicker ? 'rotate-180 text-blue-500' : ''}`} />
                </div>
              </button>

              <AnimatePresence>
                {showDatePicker && (
                  <DateBox
                    mode="range"
                    initialStartDate={startDate}
                    initialEndDate={endDate}
                    onApply={(range) => {
                      if (Array.isArray(range)) {
                        setStartDate(range[0]);
                        setEndDate(range[1]);
                        if (range[0] && range[1]) setShowDatePicker(false);
                      }
                    }}
                    onClose={() => setShowDatePicker(false)}
                  />
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="button"
              className="flex items-center justify-center gap-2 px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-sm transition-all shadow-lg shadow-blue-500/20 disabled:opacity-50"
              onClick={() => {
                setOffset(0);
                fetchTime(0);
              }}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>Searching...</span>
                </>
              ) : (
                <span>Search</span>
              )}
            </motion.button>

          </div>
        </div>

        {errMsg.err && (
          <div className="mt-4">
            <motion.p
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-red-500 text-xs font-medium"
            >
              {errMsg.msg}
            </motion.p>
          </div>
        )}
      </motion.div>


      <TimeList timesheetData={timesheetData} fetchTime={fetchTime} offset={offset} />
      {timesheetData.length > 0 && totalCount > itemsPerPage ? (
        <Pagination
          totalCount={totalCount}
          itemsPerPage={itemsPerPage}
          onPageChange={(newOffset) => {
            setOffset(newOffset);
            fetchTime(newOffset);
          }}
        />
      ) : null}

      <CSVLink
        data={prepareCSVData()}
        headers={csvHeaders}
        filename="timesheet.csv"
        ref={csvLinkRef}
        className="hidden"
      />
    </Layout>
  );
}